/**
 * useTeams — dinamik ekip listesi (Migration 0064 + 0066)
 *
 * Backend: GET /api/teams?active_only=true&role_slug=park_gorevlisi →
 *   [{ id, slug, display_name, description, role_slug, is_active,
 *     is_default, member_count, created_at, ... }, ...]
 *
 * Cache: localStorage'a kaydedilir (sadece tüm liste için; role_slug
 * filtreli istekler cache'lenmez, her zaman fresh fetch). Personel
 * formu (TeamPicker), admin ekip paneli, personel listesi badge ve
 * filtresi gibi yerlerde ortak kullanılır.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import apiClient from "../api/client";

const STORAGE_KEY = "belvizyon_teams_v1";

function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* quota dolu olabilir — sessiz geç */
  }
}

export function useTeams({ activeOnly = false, roleSlug = null, enabled = true } = {}) {
  // Cache yalnızca filtresiz istek için kullanılır (full liste)
  const useCache = !roleSlug;
  const cached = useCache ? readCache() : null;
  const [teams, setTeams] = useState(cached || []);
  const [loading, setLoading] = useState(enabled ? !cached : false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (activeOnly) params.active_only = true;
      if (roleSlug) params.role_slug = roleSlug;
      const res = await apiClient.get("/teams", { params });
      const list = Array.isArray(res.data) ? res.data : [];
      setTeams(list);
      if (!roleSlug) writeCache(list);
      return list;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [activeOnly, roleSlug]);

  useEffect(() => {
    // `enabled=false` iken (ör. `/teams` yetkisi olmayan rol) istek ATILMAZ —
    // aksi halde backend 403 döner ve tarayıcı console'una "Failed to load
    // resource" hatası düşer (Kapı 11 console/network gate ihlali).
    if (!enabled) {
      setLoading(false);
      return;
    }
    refresh().catch(() => { /* error state'te zaten */ });
  }, [refresh, enabled]);

  const sortedTeams = useMemo(() => {
    return [...teams].sort((a, b) => {
      // Default ekip her zaman en üstte
      if (a?.is_default && !b?.is_default) return -1;
      if (!a?.is_default && b?.is_default) return 1;
      return String(a?.display_name || a?.slug || "").localeCompare(
        String(b?.display_name || b?.slug || ""),
        "tr"
      );
    });
  }, [teams]);

  const activeTeams = useMemo(
    () => sortedTeams.filter((t) => t?.is_active !== false),
    [sortedTeams]
  );

  const defaultTeam = useMemo(
    () => sortedTeams.find((t) => t?.is_default) || null,
    [sortedTeams]
  );

  const teamById = useMemo(() => {
    const map = new Map();
    for (const t of teams) {
      if (t?.id) map.set(String(t.id), t);
    }
    return map;
  }, [teams]);

  const teamBySlug = useMemo(() => {
    const map = new Map();
    for (const t of teams) {
      if (t?.slug) map.set(String(t.slug), t);
    }
    return map;
  }, [teams]);

  const getTeamById = useCallback(
    (id) => teamById.get(String(id)) || null,
    [teamById]
  );
  const getTeamBySlug = useCallback(
    (slug) => teamBySlug.get(String(slug)) || null,
    [teamBySlug]
  );
  const getTeamLabel = useCallback(
    (slugOrId) => {
      if (!slugOrId) return "";
      const byId = teamById.get(String(slugOrId));
      if (byId) return byId.display_name || byId.slug || "";
      const bySlug = teamBySlug.get(String(slugOrId));
      if (bySlug) return bySlug.display_name || bySlug.slug || "";
      return String(slugOrId);
    },
    [teamById, teamBySlug]
  );

  return {
    teams: sortedTeams,
    activeTeams,
    defaultTeam,
    teamsLoading: loading,
    teamsError: error,
    getTeamById,
    getTeamBySlug,
    getTeamLabel,
    refreshTeams: refresh,
  };
}

export default useTeams;
